import { useCallback } from 'react'

import {
  type QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import { insertAtBeginningOfPage } from '@/helpers/api'
import {
  type UploadFilesPresignedResult,
  createUploadFilesPresigned,
} from '@/helpers/uploadUtils'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { markFilesIndexStartedInCache } from '@/hooks/files/cache/fileCacheSync'
import {
  pursuitActionsQueryKeys,
  pursuitFilesIndexStatusQueryKeys,
  pursuitFilesQueryKeys,
} from '@/hooks/pursuits/types'
import { boostPursuitFilesIndexPolling } from '@/hooks/pursuits/usePursuitFilesIndexStatus'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { File as UploadedFile } from '@/types/File'
import type { UUID } from '@/types/Id'

interface UseUploadPursuitFilesPresignedArgs {
  pursuitId: UUID | ''
  batchSize?: number
  onProgress?: (progress: number) => void
  rejectOnPartialFailure?: boolean
}

interface UploadPursuitFilesMutationArgs {
  files: File[]
  unrestricted?: boolean
  tag_ids?: UUID[]
  folder_id?: UUID | null
}

interface PursuitCompleteParams {
  unrestricted: boolean
  tag_ids?: UUID[]
  folder_id?: UUID | null
}

interface UploadPursuitFilesRequest {
  pursuitId: UUID | ''
  files: File[]
  unrestricted?: boolean
  tag_ids?: UUID[]
  folder_id?: UUID | null
}

type PursuitFilesSnapshot = Array<
  [QueryKey, PaginatedResponse<UploadedFile> | undefined]
>

const BATCH_SIZE = 25

const uploadFilesPresigned = createUploadFilesPresigned<PursuitCompleteParams>({
  basePath: '/api/v1/pursuits',
  entityIdName: 'pursuit ID',
  buildBatchCompleteBody: (s3Keys, { unrestricted, tag_ids, folder_id }) => ({
    uploads: s3Keys.map((s3Key) => ({ s3_key: s3Key })),
    unrestricted,
    tag_ids,
    folder_id: folder_id ?? undefined,
  }),
  useRetry: true,
  progressAfterComplete: false, // Pursuit tracks progress after S3 upload
})

const useUploadPursuitFilesMutationHandlers = <T>() => {
  const queryClient = useQueryClient()
  const { handleError } = useHandleError()

  const onMutate = (pursuitId: UUID | '') => async () => {
    const key = pursuitFilesQueryKeys.pursuit({ pursuitId })
    await queryClient.cancelQueries({ queryKey: key })
    boostPursuitFilesIndexPolling({ queryClient, pursuitId })
    return queryClient.getQueriesData<PaginatedResponse<UploadedFile>>({
      queryKey: key,
    })
  }

  const onError = (
    err: Error,
    _req: T,
    context: PursuitFilesSnapshot | undefined,
  ) => {
    if (context) {
      for (const [queryKey, previousData] of context) {
        queryClient.setQueryData(queryKey, previousData)
      }
    }
    handleError(err)
  }

  const onSuccess =
    (pursuitId: UUID | '') => (data: UploadFilesPresignedResult, _req: T) => {
      const key = pursuitFilesQueryKeys.pursuit({ pursuitId })
      queryClient.setQueriesData<PaginatedResponse<UploadedFile>>(
        { queryKey: key },
        (oldData) =>
          insertAtBeginningOfPage(
            { data: data.created, count: data.created.length, has_more: false },
            oldData,
          ),
      )

      const actionsKey = pursuitActionsQueryKeys.pursuit({ pursuitId })
      queryClient.invalidateQueries({ queryKey: actionsKey })

      const filesAwaitingIndex = data.created.filter(
        (file) =>
          file.index_status !== 'failed' && file.index_status !== 'aborted',
      )
      markFilesIndexStartedInCache({
        queryClient,
        fileIds: filesAwaitingIndex.map((file) => file.id),
        pursuitId,
      })
      queryClient.invalidateQueries({
        queryKey: pursuitFilesIndexStatusQueryKeys.all(),
      })

      if (data.failed.length > 0) {
        toast.error(
          `${data.failed.length} of ${data.created.length + data.failed.length} files could not be fully processed. Successful uploads were kept.`,
        )
      }
    }

  return { onMutate, onError, onSuccess }
}

export const useUploadPursuitFilesPresigned = ({
  pursuitId,
  batchSize = BATCH_SIZE,
  onProgress,
  rejectOnPartialFailure = false,
}: UseUploadPursuitFilesPresignedArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { onMutate, onError, onSuccess } =
    useUploadPursuitFilesMutationHandlers<UploadPursuitFilesMutationArgs>()
  const mutationFn = useCallback(
    ({
      files,
      unrestricted = true,
      tag_ids,
      folder_id,
    }: UploadPursuitFilesMutationArgs) =>
      uploadFilesPresigned(
        pursuitId,
        files,
        accessToken,
        { unrestricted, tag_ids, folder_id },
        batchSize,
        onProgress,
        { rejectOnPartialFailure },
      ),
    [pursuitId, accessToken, batchSize, onProgress, rejectOnPartialFailure],
  )
  const onMutateCb = useCallback(
    (_req: UploadPursuitFilesMutationArgs) => onMutate(pursuitId)(),
    [onMutate, pursuitId],
  )
  const onErrorCb = useCallback(
    (
      err: Error,
      req: UploadPursuitFilesMutationArgs,
      context: PursuitFilesSnapshot | undefined,
    ) => onError(err, req, context),
    [onError],
  )
  const onSuccessCb = useCallback(
    (data: UploadFilesPresignedResult, req: UploadPursuitFilesMutationArgs) =>
      onSuccess(pursuitId)(data, req),
    [onSuccess, pursuitId],
  )

  return useMutation<
    UploadFilesPresignedResult,
    Error,
    UploadPursuitFilesMutationArgs,
    PursuitFilesSnapshot
  >({
    mutationFn,
    onMutate: onMutateCb,
    onError: onErrorCb,
    onSuccess: onSuccessCb,
  })
}

export const useUploadPursuitFilesPresignedWithoutPursuitId = () => {
  const { accessToken } = useAuthInfoWithThrow()
  const { onMutate, onError, onSuccess } =
    useUploadPursuitFilesMutationHandlers<UploadPursuitFilesRequest>()
  const mutationFn = useCallback(
    ({
      pursuitId,
      files,
      unrestricted = true,
      tag_ids,
      folder_id,
    }: UploadPursuitFilesRequest) =>
      uploadFilesPresigned(
        pursuitId,
        files,
        accessToken,
        { unrestricted, tag_ids, folder_id },
        BATCH_SIZE,
        undefined,
        { rejectOnPartialFailure: false },
      ),
    [accessToken],
  )
  const onMutateCb = useCallback(
    ({ pursuitId }: UploadPursuitFilesRequest) => onMutate(pursuitId)(),
    [onMutate],
  )
  const onErrorCb = useCallback(
    (
      err: Error,
      req: UploadPursuitFilesRequest,
      context: PursuitFilesSnapshot | undefined,
    ) => onError(err, req, context),
    [onError],
  )
  const onSuccessCb = useCallback(
    (data: UploadFilesPresignedResult, req: UploadPursuitFilesRequest) =>
      onSuccess(req.pursuitId)(data, req),
    [onSuccess],
  )

  return useMutation<
    UploadFilesPresignedResult,
    Error,
    UploadPursuitFilesRequest,
    PursuitFilesSnapshot
  >({
    mutationFn,
    onMutate: onMutateCb,
    onError: onErrorCb,
    onSuccess: onSuccessCb,
  })
}
